import { ChangeEventHandler, useEffect, useMemo, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { Tabs, Input, Modal, EmptyState, Button } from '@ets/design-system';

import { useDispatch, useSelector } from '_common/hooks/redux';
import { useDebounce } from '_common/hooks';
import { closeModal } from 'App/ModalContext/utils';
import { toast } from 'utils/toast';

import {
  deletePersonalSavedFilter,
  FilterObject,
  loadPersonalSavedFilters,
  SearchPageSliceState,
  setAdvancedFilters,
  setAppliedFilterSet,
  setSearchMode,
  setSearchQuery,
} from 'Search/redux/SearchPageSlice';

import RemovableItem from './RemovableItem/RemovableItem';
import useOldFilterParser from './useOldFilterParser';

import styles from './AccessSavedFiltersModal.module.scss';

const MODAL = 'AccessSavedFiltersModal';
const SEARCH_LIMIT = 10;
const TABS = {
  personal: { id: 'personal', label: 'PERSONAL_FILTER' },
  // Disabled because it hasn't been developed yet
  // tenant: { id: 'tenant', label: 'TENANT_FILTER' },
};

const AccessSavedFiltersModal = () => {
  const intl = useIntl();
  const dispatch = useDispatch();
  const oldFilterParser = useOldFilterParser();

  const personalFilters = useSelector((state) => state.search.savedFilters.personal);

  const [filterSetSearch, setFilterSetSearch] = useState('');
  const [selectedTab, setSelectedTab] = useState(Object.values(TABS)[0].id);
  const [selectedFilterId, setSelectedFilterId] = useState<FilterObject['id'] | undefined>(
    undefined,
  );
  const [removingFilterId, setRemovingFilterId] = useState<FilterObject['id'] | undefined>(
    undefined,
  );

  const debouncedFilterValue = useDebounce(filterSetSearch, 250);
  const filteredList = useMemo(() => {
    const tempList = Object.values(personalFilters).filter((item) =>
      item.name.toLowerCase().includes(filterSetSearch.toLowerCase()),
    );

    // Selected filter will no longer be selected in it doesn't exist in the filtered list
    if (!tempList.some((item) => item.id === selectedTab)) {
      setSelectedFilterId(undefined);
    }
    return tempList;
  }, [debouncedFilterValue, personalFilters]);

  useEffect(() => {
    dispatch(loadPersonalSavedFilters());
  }, []);

  //#region UI Handles
  const handleClose = () => {
    setFilterSetSearch('');
    setRemovingFilterId(undefined);
    setSelectedFilterId(undefined);

    closeModal(MODAL);
  };

  const handleFilterSetSearchChange: ChangeEventHandler<HTMLInputElement> = (e) => {
    setFilterSetSearch(e.target.value);
  };

  const handleCancel = () => setRemovingFilterId(undefined);

  const handleStartDelete = (savedFilterId: FilterObject['id']) => {
    setRemovingFilterId(savedFilterId);
    setSelectedFilterId(undefined);
  };

  const handleChangeTab = (tabId: string) => {
    setSelectedTab(tabId);
    setSelectedFilterId(undefined);
  };
  //#endregion

  const handleDelete = () => {
    if (removingFilterId) {
      dispatch(
        deletePersonalSavedFilter({
          filter: personalFilters[removingFilterId],
        }),
      );
    }

    setRemovingFilterId(undefined);
  };

  const handleApplyFilterSet = () => {
    if (selectedFilterId) {
      const selectedFilter = personalFilters[selectedFilterId];

      if (Array.isArray(selectedFilter.filters)) {
        const indexedParsedFilters: SearchPageSliceState['advancedFilters'] = {};

        selectedFilter.filters.forEach((filter) => {
          const parsedFilter = oldFilterParser(filter);
          if (parsedFilter) {
            indexedParsedFilters[parsedFilter.id] = { ...parsedFilter, unprocessed: true };
          }
        });
        dispatch(setAppliedFilterSet(selectedFilter.name));
        dispatch(setSearchMode('normal'));
        dispatch(setAdvancedFilters(indexedParsedFilters));
      } else {
        dispatch(setSearchMode('advanced'));
        dispatch(setSearchQuery({ query: selectedFilter.filters }));
      }

      toast({
        variant: 'success',
        title: 'COMBINATION_FILTERS_APPLIED',
        message: 'FILTER_APPLIED_IN_THE_LIST',
        messageValues: { filterName: selectedFilter.name },
      });

      handleClose();
    }
  };

  const renderPersonalContent = () => {
    // Doesn't have personal filters
    if (Object.keys(personalFilters).length === 0) {
      return (
        <div className={styles.emptyStateWrapper}>
          <EmptyState
            size="m"
            title={intl.formatMessage({
              id: 'NO_PERSONAL_SAVED_FILTERS',
            })}
            testId="no-personal-saved-filters"
            description={intl.formatMessage({
              id: 'SAVE_A_PERSONAL_FILTER_AND_WILL_BE_DISPLAYED_HERE',
            })}
          />
        </div>
      );
    }

    // Doesn't have personal filters following the search input filter
    if (filteredList.length === 0) {
      return (
        <div className={styles.emptyStateWrapper}>
          <EmptyState
            size="m"
            title={intl.formatMessage({
              id: 'NO_RESULTS_FOUND',
            })}
            testId="no-results-found"
            description={intl.formatMessage({
              id: 'NO_FILTERS_MATCH_FILTER',
            })}
          />
        </div>
      );
    }

    // Render tab content
    return (
      <div className={styles.filterSetsContainer}>
        {filteredList.map((item: FilterObject, index) => (
          <RemovableItem
            key={`saved-filter-item-${item.id}`}
            prefixIcon="filters"
            savedFilter={item}
            selected={item.id === selectedFilterId}
            beingRemoved={item.id === removingFilterId}
            onSelect={setSelectedFilterId}
            onCancel={handleCancel}
            onDelete={handleDelete}
            onStartDelete={handleStartDelete}
            testId={`advanced-search-view-saved-filters-modal-item-${index}`}
          />
        ))}
      </div>
    );
  };

  // Disabled because it hasn't been developed yet
  /*  const renderTenantContent = () => {
    //Missing figma mockup definition
    return null;
  }; */

  return (
    <Modal open width="75rem" close={handleClose} testId="advanced-search-view-saved-filters">
      <Modal.Header>
        <FormattedMessage id="SAVED_FILTERS" />
      </Modal.Header>
      <Modal.Body style={{ overflow: 'visible' }}>
        <Tabs value={selectedTab} onValueChange={handleChangeTab}>
          <Tabs.List>
            <Tabs.Trigger value={TABS.personal.id}>
              <FormattedMessage id={TABS.personal.label} />
            </Tabs.Trigger>
            {/* <Tabs.Tigger value={TABS.tenant.id}>
              <FormattedMessage id={TABS.tenant.label} />
            </Tabs.Tigger> */}
          </Tabs.List>
          {Object.keys(personalFilters).length > SEARCH_LIMIT && (
            <Input
              value={filterSetSearch}
              onChange={handleFilterSetSearchChange}
              size="l"
              placeholder={intl.formatMessage({ id: 'SEARCH_SAVED_FILTERS' })}
              prefix="magnifying-glass"
              margin="3rem 0 3rem 0"
              testId="saved-filters-search"
            />
          )}
          <Tabs.Content value={TABS.personal.id}>{renderPersonalContent()}</Tabs.Content>
          {/* <Tabs.Content value={TABS.tenant.id}>{renderTenantContent()}</Tabs.Content> */}
        </Tabs>
      </Modal.Body>

      <Modal.Footer>
        <Button
          size="m"
          variant="standard"
          onClick={handleClose}
          testId="advanced-search-view-saved-filters-modal-close"
        >
          <FormattedMessage id="global.cancel" />
        </Button>
        <Button
          size="m"
          style={{ margin: '0 0 0 1rem' }}
          disabled={!selectedFilterId}
          variant="primary"
          onClick={handleApplyFilterSet}
          testId="advanced-search-view-saved-filters-modal-apply"
        >
          <FormattedMessage id="APPLY_FILTERS_TO_LIST" />
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default AccessSavedFiltersModal;
